// path: frontend/src/components/Modals/EditEmployeeModal.jsx
/**
 * Редактирование сотрудника. Поле «Новый пароль» доступно только
 * администратору — capabilities.editRoles (сервер:
 * requireAdminOrHr в employee.routes + EmployeeService.save). Сервер
 * всё равно проверяет; capabilities — UX-зеркало.
 *
 * Форма создаётся с key={emp.id}: при смене сотрудника пересоздаётся.
 */
import { useCallback, useMemo } from "react";
import { ModalShell } from "../ModalShell";
import { EmployeeFormFields } from "../EmployeeFormFields";
import { useModalForm } from "../../hooks/useModalForm";
import { useAuth } from "../../hooks/useAuth";
import {
  useEmployeeQuery,
  useUpdateEmployeeMutation,
} from "../../api/queries/employees";

const FIELDS = Object.freeze([
  "last",
  "first",
  "email",
  "position",
  "extension",
  "tab",
  "newPass",
]);

export const EditEmployeeModal = ({ employeeId, onClose, toast }) => {
  const employeeQ = useEmployeeQuery(employeeId);
  const emp = employeeQ.data;

  if (!emp) {
    return (
      <ModalShell
        title="Редактирование сотрудника"
        onClose={onClose}
        width={560}
        className="modal-employee"
        showSave={false}
        footer={
          <div className="modal-foot">
            <div className="spacer" />
            <button type="button" className="btn ghost" onClick={onClose}>
              Закрыть
            </button>
          </div>
        }
      >
        <div className="empty-note p-4">
          {employeeQ.isError
            ? "Не удалось загрузить данные сотрудника"
            : "Загрузка…"}
        </div>
      </ModalShell>
    );
  }

  return (
    <EditEmployeeForm
      key={emp.id}
      emp={emp}
      employeeId={employeeId}
      onClose={onClose}
      toast={toast}
    />
  );
};

const EditEmployeeForm = ({ emp, employeeId, onClose, toast }) => {
  const { capabilities } = useAuth();
  const updateMutation = useUpdateEmployeeMutation();
  const isAdmin = Boolean(capabilities.editRoles);

  const initialValues = useMemo(
    () => ({
      last: emp.last || "",
      first: emp.first || "",
      email: emp.email || "",
      position: emp.position || "Сотрудник",
      extension: emp.extension || "",
      tab: emp.tab || "",
      newPass: "",
    }),
    [emp],
  );

  const validate = useCallback((values) => {
    const errors = {};
    if (!values.last.trim()) errors.last = "Фамилия обязательна";
    if (!values.first.trim()) errors.first = "Имя обязательно";
    if (!values.email.trim()) errors.email = "E-mail обязателен";
    if (values.newPass && values.newPass.length < 8)
      errors.newPass = "Пароль должен быть не менее 8 символов";
    return errors;
  }, []);

  const saveAsync = useCallback(
    async (vals) => {
      const body = {
        last: vals.last.trim(),
        first: vals.first.trim(),
        email: vals.email.trim(),
        position: vals.position.trim() || "Сотрудник",
        extension: vals.extension || "",
        tabNumber: vals.tab || null,
      };
      if (isAdmin && vals.newPass.trim()) {
        body.password = vals.newPass.trim();
      }
      await updateMutation.mutateAsync({ id: employeeId, body });
      onClose();
    },
    [isAdmin, updateMutation, onClose, employeeId],
  );

  const { values, handleChange, errors, touched, handleSubmit, isSubmitting } =
    useModalForm(
      initialValues,
      validate,
      saveAsync,
      (error) => {
        toast(error.message || "Ошибка редактирования сотрудника", "error");
      },
      { fields: FIELDS },
    );

  const effRate = emp.effectiveRate ?? 1;

  return (
    <ModalShell
      title={`Редактирование сотрудника - ${emp.last} ${emp.first}`}
      onClose={onClose}
      onSave={handleSubmit()}
      saveLabel="Сохранить"
      width={560}
      className="modal-employee"
      saveDisabled={isSubmitting}
    >
      <EmployeeFormFields
        values={values}
        onChange={handleChange}
        errors={errors}
        touched={touched}
        showPassword={isAdmin}
      />
      {effRate !== 1 && (
        <div className="info-box mt-3">
          Эффективная ставка: <b>{effRate} ст.</b>
        </div>
      )}
    </ModalShell>
  );
};
